export type ResolveImageAltInput = {
    /** Explicit alt from the caller. Empty string means decorative when allowEmptyDecorative is true. */
    alt?: string | null;
    title?: string | null;
    name?: string | null;
    /** Final safety-net fallback (often localized, then English). */
    fallback?: string;
    /**
     * When true (default), an explicit empty `alt=""` is preserved for decorative images.
     * When false, empty alt continues through the fallback chain.
     */
    allowEmptyDecorative?: boolean;
};

function firstNonEmpty(...values: Array<string | null | undefined>): string | undefined {
    for (const value of values) {
        if (typeof value !== 'string') continue;
        const trimmed = value.trim();
        if (trimmed) return trimmed;
    }
    return undefined;
}

/**
 * Resolve accessible image alt text with a consistent fallback order:
 * alt → title → name → fallback (default "Product Image").
 *
 * Explicit `alt=""` is preserved as decorative when `allowEmptyDecorative` is true (default).
 */
export function resolveImageAlt({
    alt,
    title,
    name,
    fallback = 'Product Image',
    allowEmptyDecorative = true,
}: ResolveImageAltInput): string {
    if (allowEmptyDecorative && alt === '') {
        return '';
    }

    return firstNonEmpty(alt, title, name, fallback) ?? 'Product Image';
}

/**
 * Pull alt/title/name-like fields from SCAPI / Page Designer image objects.
 */
export function extractImageAltFields(image: Record<string, unknown> | null | undefined): {
    alt?: string;
    title?: string;
    name?: string;
} {
    if (!image || typeof image !== 'object') {
        return {};
    }

    const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

    return {
        alt: asString(image.alt),
        title: asString(image.title),
        name: asString(image.name) ?? asString(image.filename) ?? asString(image.fileName) ?? asString(image.file),
    };
}
